import wadaColors from "../wada-colors.json";
import ralColors from "../ral-colors.json";
import hksColors from "../hks-colors.json";
import copicColors from "../copic-colors.json";
import pantoneColors from "../pantone-colors.json";
import ikeaColors from "../ikea-colors.json";
import prismacolorColors from "../prismacolor-colors.json";
import materialDesignColors from "../material-design-colors.json";
import tailwindColors from "../tailwind-colors.json";
import ncsColors from "../ncs-colors.json";
import federalStandardColors from "../federal-standard-595-colors.json";
import britishStandardColors from "../british-standard-colors.json";
import munsellColors from "../munsell-colors.json";
import automotiveColors from "../automotive-colors.json";
import crayolaColors from "../crayola-colors.json";
import sherwinWilliamsColors from "../sherwin-williams-colors.json";
import benjaminMooreColors from "../benjamin-moore-colors.json";
import duluxColors from "../dulux-colors.json";
import reseneColors from "../resene-colors.json";
import valsparColors from "../valspar-colors.json";
import behrColors from "../behr-colors.json";
import ppgColors from "../ppg-colors.json";
import australianStandardColors from "../australian-standard-colors.json";
import pittsburghPaintsColors from "../pittsburgh-paints-colors.json";
import gliddenColors from "../glidden-colors.json";
import winsorNewtonColors from "../winsor-newton-colors.json";

// ============= BASIC COLOR UTILITIES =============

export const hueShift = (h, s) => (h + s) % 360;

export const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

export const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, "");
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
};

export const rgbToHex = (r, g, b) => {
  return "#" + [r, g, b]
    .map((x) => {
      const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("");
};

export const generateRandomColor = () => {
  return "#" + Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0");
};

// ============= PALETTE GENERATION =============

export const generatePalettes = (baseColor) => {
  const [h, s, l] = hexToHsl(baseColor);

  const createPalette = (angles) => {
    return [
      baseColor,
      ...angles.slice(1).map((angle) => hslToHex(hueShift(h, angle), s, l)),
    ];
  };

  return {
    monochromatic: [
      hslToHex(h, s, Math.max(0, l - 40)),
      hslToHex(h, s, Math.max(0, l - 20)),
      baseColor,
      hslToHex(h, s, Math.min(100, l + 20)),
      hslToHex(h, s, Math.min(100, l + 40)),
    ],
    analogous: createPalette([0, 30, 60, -30, -60]),
    complementary: createPalette([0, 180, 30, 210, 150]),
    triad: createPalette([0, 120, 240, 60, 300]),
    tetrad: createPalette([0, 90, 180, 270, 45]),
    splitComplementary: createPalette([0, 150, 210, 180, 30]),
  };
};

export const generateShades = (baseColor) => {
  const [baseH, baseS, baseL] = hexToHsl(baseColor);
  const shadeNumbers = [1000, 900, 800, 700, 600, 500, 400, 300, 200, 100, 50, 0];

  let shades = shadeNumbers.map((number) => {
    const lightness = Math.round((1000 - number) / 10);
    return { number, color: hslToHex(baseH, baseS, lightness) };
  });

  const closestShadeIndex = shades.reduce(
    (closestIndex, shade, currentIndex, arr) =>
      Math.abs(hexToHsl(shade.color)[2] - baseL) <
        Math.abs(hexToHsl(arr[closestIndex].color)[2] - baseL)
        ? currentIndex
        : closestIndex,
    0,
  );

  shades[closestShadeIndex] = {
    number: shades[closestShadeIndex].number,
    color: baseColor,
  };

  return shades;
};

// ============= MOOD-BASED PALETTE GENERATION =============

// Mood presets with their associated color characteristics
export const MOOD_PRESETS = {
  energetic: {
    name: 'Energetic',
    description: 'Vibrant, high-energy colors that evoke action and excitement',
    hues: [0, 15, 30, 45], // Reds, oranges, yellows
    saturation: [70, 95],
    lightness: [45, 70],
    harmony: 'analogous'
  },
  calm: {
    name: 'Calm',
    description: 'Peaceful, relaxing colors that create tranquility',
    hues: [180, 200, 220, 240], // Blues, teals
    saturation: [25, 50],
    lightness: [60, 85],
    harmony: 'monochromatic'
  },
  professional: {
    name: 'Professional',
    description: 'Corporate, trustworthy colors for business contexts',
    hues: [210, 220, 230, 0], // Blues and neutrals
    saturation: [30, 60],
    lightness: [35, 65],
    harmony: 'monochromatic'
  },
  luxurious: {
    name: 'Luxurious',
    description: 'Rich, premium colors that convey sophistication',
    hues: [270, 280, 45, 0], // Purples, golds, blacks
    saturation: [40, 80],
    lightness: [25, 55],
    harmony: 'split'
  },
  natural: {
    name: 'Natural',
    description: 'Earthy, organic colors inspired by nature',
    hues: [90, 110, 130, 35], // Greens, browns
    saturation: [35, 65],
    lightness: [40, 70],
    harmony: 'analogous'
  },
  creative: {
    name: 'Creative',
    description: 'Playful, imaginative colors that inspire innovation',
    hues: [280, 320, 30, 180], // Purples, pinks, oranges, cyans
    saturation: [60, 90],
    lightness: [50, 75],
    harmony: 'tetrad'
  },
  trustworthy: {
    name: 'Trustworthy',
    description: 'Stable, reliable colors that build confidence',
    hues: [200, 210, 220, 170], // Blues, teals
    saturation: [45, 70],
    lightness: [40, 65],
    harmony: 'analogous'
  },
  modern: {
    name: 'Modern',
    description: 'Sleek, contemporary colors with minimal aesthetics',
    hues: [0, 210, 0, 0], // Grays, cool tones
    saturation: [5, 25],
    lightness: [25, 75],
    harmony: 'monochromatic'
  },
  friendly: {
    name: 'Friendly',
    description: 'Warm, welcoming colors that feel approachable',
    hues: [30, 40, 20, 50], // Warm oranges, yellows
    saturation: [50, 75],
    lightness: [55, 80],
    harmony: 'analogous'
  },
  elegant: {
    name: 'Elegant',
    description: 'Refined, sophisticated colors with timeless appeal',
    hues: [0, 280, 40, 0], // Blacks, purples, golds
    saturation: [15, 45],
    lightness: [20, 70],
    harmony: 'complementary'
  },
  spiritual: {
    name: 'Spiritual',
    description: 'Ethereal, mystical colors for introspection',
    hues: [260, 280, 300, 240], // Deep purples, violets
    saturation: [40, 70],
    lightness: [35, 65],
    harmony: 'analogous'
  },
  bold: {
    name: 'Bold',
    description: 'Strong, confident colors with high impact',
    hues: [0, 180, 60, 300], // Reds, cyans, yellows, magentas
    saturation: [75, 95],
    lightness: [35, 60],
    harmony: 'tetrad'
  },
  playful: {
    name: 'Playful',
    description: 'Fun, cheerful colors for youthful and energetic brands',
    hues: [300, 330, 50, 180], // Pinks, corals, yellows, cyans
    saturation: [65, 90],
    lightness: [55, 80],
    harmony: 'tetrad'
  },
  minimalist: {
    name: 'Minimalist',
    description: 'Clean, restrained colors with subtle sophistication',
    hues: [0, 200, 0, 0], // Neutrals and cool grays
    saturation: [0, 15],
    lightness: [30, 85],
    harmony: 'monochromatic'
  },
  vintage: {
    name: 'Vintage',
    description: 'Nostalgic, heritage colors with warm, muted tones',
    hues: [25, 35, 15, 45], // Warm browns, oranges, yellows
    saturation: [30, 55],
    lightness: [40, 65],
    harmony: 'analogous'
  },
  warm: {
    name: 'Warm',
    description: 'Inviting, cozy colors for hospitality and comfort',
    hues: [10, 20, 30, 40], // Reds, oranges, warm yellows
    saturation: [50, 75],
    lightness: [50, 75],
    harmony: 'analogous'
  },
  fresh: {
    name: 'Fresh',
    description: 'Clean, vibrant colors that evoke renewal and vitality',
    hues: [120, 140, 170, 190], // Greens, teals, blues
    saturation: [55, 80],
    lightness: [50, 75],
    harmony: 'analogous'
  },
  sophisticated: {
    name: 'Sophisticated',
    description: 'Polished, upscale colors with contemporary refinement',
    hues: [0, 210, 280, 40], // Charcoals, navy, purples, golds
    saturation: [20, 50],
    lightness: [25, 65],
    harmony: 'split'
  }
};

// Generate a palette based on mood/semantic meaning
export const generateMoodPalette = (mood, colorCount = 5) => {
  const preset = MOOD_PRESETS[mood];
  if (!preset) {
    console.warn(`Unknown mood preset: ${mood}`);
    return [];
  }

  const colors = [];
  const { hues, saturation, lightness, harmony } = preset;

  // Helper to get random value in range
  const randomInRange = (min, max) => min + Math.random() * (max - min);

  // Helper to clamp values
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  // Generate base colors based on harmony type
  switch (harmony) {
    case 'monochromatic': {
      // All colors share same hue, vary in saturation and lightness
      const baseHue = hues[Math.floor(Math.random() * hues.length)];
      const lightnessStep = (lightness[1] - lightness[0]) / (colorCount - 1);

      for (let i = 0; i < colorCount; i++) {
        const l = lightness[0] + (lightnessStep * i);
        const s = randomInRange(saturation[0], saturation[1]);
        colors.push(hslToHex(baseHue, s, l));
      }
      break;
    }

    case 'analogous': {
      // Colors are adjacent on color wheel
      const baseHue = hues[0];
      const hueStep = 30; // 30 degrees apart

      for (let i = 0; i < colorCount; i++) {
        const h = (baseHue + (hueStep * i)) % 360;
        const s = randomInRange(saturation[0], saturation[1]);
        const l = randomInRange(lightness[0], lightness[1]);
        colors.push(hslToHex(h, s, l));
      }
      break;
    }

    case 'complementary': {
      // Pairs of opposite colors
      for (let i = 0; i < colorCount; i++) {
        const hueIndex = i % 2; // Alternate between two hues
        const h = hues[hueIndex] || hues[0];
        const s = randomInRange(saturation[0], saturation[1]);
        const l = randomInRange(lightness[0], lightness[1]);
        colors.push(hslToHex(h, s, l));
      }
      break;
    }

    case 'split': {
      // Split complementary - base + two adjacent to complement
      const baseHue = hues[0];
      const complement = (baseHue + 180) % 360;
      const split1 = (complement + 30) % 360;
      const split2 = (complement - 30 + 360) % 360;
      const paletteHues = [baseHue, split1, split2];

      for (let i = 0; i < colorCount; i++) {
        const h = paletteHues[i % paletteHues.length];
        const s = randomInRange(saturation[0], saturation[1]);
        const l = randomInRange(lightness[0], lightness[1]);
        colors.push(hslToHex(h, s, l));
      }
      break;
    }

    case 'tetrad': {
      // Four colors evenly spaced
      for (let i = 0; i < colorCount; i++) {
        const h = hues[i % hues.length];
        const s = randomInRange(saturation[0], saturation[1]);
        const l = randomInRange(lightness[0], lightness[1]);
        colors.push(hslToHex(h, s, l));
      }
      break;
    }

    default: {
      // Fallback - use provided hues with variation
      for (let i = 0; i < colorCount; i++) {
        const h = hues[i % hues.length];
        const s = randomInRange(saturation[0], saturation[1]);
        const l = randomInRange(lightness[0], lightness[1]);
        colors.push(hslToHex(h, s, l));
      }
    }
  }

  return colors;
};

// Get all available mood presets
export const getMoodPresets = () => {
  return Object.keys(MOOD_PRESETS).map(key => ({
    id: key,
    name: MOOD_PRESETS[key].name,
    description: MOOD_PRESETS[key].description
  }));
};

// ============= COLOR SPACE CONVERSIONS =============

// Convert sRGB to linear RGB
const sRGBToLinear = (c) => {
  c = c / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

// Convert RGB to XYZ color space (D65 illuminant)
export const rgbToXYZ = (r, g, b) => {
  const rLinear = sRGBToLinear(r);
  const gLinear = sRGBToLinear(g);
  const bLinear = sRGBToLinear(b);

  const x = 0.4124564 * rLinear + 0.3575761 * gLinear + 0.1804375 * bLinear;
  const y = 0.2126729 * rLinear + 0.7151522 * gLinear + 0.0721750 * bLinear;
  const z = 0.0193339 * rLinear + 0.1191920 * gLinear + 0.9503041 * bLinear;

  return [x * 100, y * 100, z * 100];
};

// Convert XYZ to LAB color space
export const xyzToLAB = (x, y, z) => {
  // D65 illuminant reference values
  const refX = 95.047;
  const refY = 100.000;
  const refZ = 108.883;

  x = x / refX;
  y = y / refY;
  z = z / refZ;

  const fx = x > 0.008856 ? Math.pow(x, 1 / 3) : (7.787 * x) + (16 / 116);
  const fy = y > 0.008856 ? Math.pow(y, 1 / 3) : (7.787 * y) + (16 / 116);
  const fz = z > 0.008856 ? Math.pow(z, 1 / 3) : (7.787 * z) + (16 / 116);

  const L = (116 * fy) - 16;
  const A = 500 * (fx - fy);
  const B = 200 * (fy - fz);

  return [L, A, B];
};

export const hexToLAB = (hex) => {
  const rgb = hexToRgb(hex);
  const xyz = rgbToXYZ(rgb[0], rgb[1], rgb[2]);
  return xyzToLAB(xyz[0], xyz[1], xyz[2]);
};

// LCH Color Space
export const hexToLCH = (hex) => {
  const lab = hexToLAB(hex);
  const [l, a, b] = lab;
  const c = Math.sqrt(a * a + b * b);
  const h = Math.atan2(b, a) * (180 / Math.PI);
  const hNormalized = h < 0 ? h + 360 : h;
  return [l, c, hNormalized];
};

// OKLAB Color Space
const linearSRGBtoOKLAB = (r, g, b) => {
  const toLinear = (c) => {
    c = c / 255;
    return c > 0.04045 ? Math.pow((c + 0.055) / 1.055, 2.4) : c / 12.92;
  };

  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return [
    0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  ];
};

export const hexToOKLAB = (hex) => {
  const rgb = hexToRgb(hex);
  return linearSRGBtoOKLAB(rgb[0], rgb[1], rgb[2]);
};

export const hexToOKLCH = (hex) => {
  const [l, a, b] = hexToOKLAB(hex);
  const c = Math.sqrt(a * a + b * b);
  const h = Math.atan2(b, a) * (180 / Math.PI);
  const hNormalized = h < 0 ? h + 360 : h;
  return [l, c, hNormalized];
};

// HSB/HSV Color Space
export const rgbToHSB = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h, s, v = max;

  s = max === 0 ? 0 : d / max;

  if (max === min) {
    h = 0;
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(v * 100)];
};

// CMYK Color Space
export const rgbToCMYK = (r, g, b) => {
  let c = 1 - r / 255;
  let m = 1 - g / 255;
  let y = 1 - b / 255;
  let k = Math.min(c, m, y);

  if (k === 1) {
    return [0, 0, 0, 100];
  }

  c = Math.round(((c - k) / (1 - k)) * 100);
  m = Math.round(((m - k) / (1 - k)) * 100);
  y = Math.round(((y - k) / (1 - k)) * 100);
  k = Math.round(k * 100);

  return [c, m, y, k];
};

// Reverse Conversions for Palette Generation

// OKLAB to RGB
export const oklabToRgb = (l, a, b) => {
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.2914855480 * b;

  const l3 = l_ * l_ * l_;
  const m3 = m_ * m_ * m_;
  const s3 = s_ * s_ * s_;

  const lr = 4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
  const lg = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
  const lb = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.7076147010 * s3;

  const fromLinear = (c) => {
    return c > 0.0031308 ? 1.055 * Math.pow(c, 1 / 2.4) - 0.055 : 12.92 * c;
  };

  const r = Math.round(Math.max(0, Math.min(1, fromLinear(lr))) * 255);
  const g = Math.round(Math.max(0, Math.min(1, fromLinear(lg))) * 255);
  const b_out = Math.round(Math.max(0, Math.min(1, fromLinear(lb))) * 255);

  return [r, g, b_out];
};

// OKLCH to RGB
export const oklchToRgb = (l, c, h) => {
  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);
  return oklabToRgb(l, a, b);
};

// OKLCH to HEX
export const oklchToHex = (l, c, h) => {
  const rgb = oklchToRgb(l, c, h);
  return rgbToHex(rgb[0], rgb[1], rgb[2]);
};

// LAB to XYZ
export const labToXYZ = (l, a, b) => {
  const refX = 95.047;
  const refY = 100.000;
  const refZ = 108.883;

  const fy = (l + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;

  const xr = fx * fx * fx > 0.008856 ? fx * fx * fx : (fx - 16 / 116) / 7.787;
  const yr = fy * fy * fy > 0.008856 ? fy * fy * fy : (fy - 16 / 116) / 7.787;
  const zr = fz * fz * fz > 0.008856 ? fz * fz * fz : (fz - 16 / 116) / 7.787;

  return [xr * refX, yr * refY, zr * refZ];
};

// XYZ to RGB
export const xyzToRgb = (x, y, z) => {
  x = x / 100;
  y = y / 100;
  z = z / 100;

  let r = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
  let g = x * -0.9692660 + y * 1.8760108 + z * 0.0415560;
  let b = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;

  const fromLinear = (c) => {
    return c > 0.0031308 ? 1.055 * Math.pow(c, 1 / 2.4) - 0.055 : 12.92 * c;
  };

  r = Math.round(Math.max(0, Math.min(1, fromLinear(r))) * 255);
  g = Math.round(Math.max(0, Math.min(1, fromLinear(g))) * 255);
  b = Math.round(Math.max(0, Math.min(1, fromLinear(b))) * 255);

  return [r, g, b];
};

// LCH to LAB
export const lchToLab = (l, c, h) => {
  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);
  return [l, a, b];
};

// LCH to HEX
export const lchToHex = (l, c, h) => {
  const lab = lchToLab(l, c, h);
  const xyz = labToXYZ(lab[0], lab[1], lab[2]);
  const rgb = xyzToRgb(xyz[0], xyz[1], xyz[2]);
  return rgbToHex(rgb[0], rgb[1], rgb[2]);
};

// ============= LUMINANCE AND CONTRAST =============

export const getRelativeLuminance = (r, g, b) => {
  const [R, G, B] = [r, g, b].map((c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

export const getLuminance = (r, g, b) => {
  return getRelativeLuminance(r, g, b);
};

export const getContrastRatio = (rgb1, rgb2) => {
  const lum1 = getLuminance(rgb1[0], rgb1[1], rgb1[2]);
  const lum2 = getLuminance(rgb2[0], rgb2[1], rgb2[2]);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
};

// ============= COLOR NAMING =============

export const getWadaColorName = (h, s, l) => {
  const targetHsl = [h, s, l];

  const colorDifference = (color1, color2) => {
    return Math.sqrt(
      Math.pow(color1[0] - color2[0], 2) +
      Math.pow(color1[1] - color2[1], 2) +
      Math.pow(color1[2] - color2[2], 2),
    );
  };

  let closestColor = wadaColors.colors.reduce((closest, current) => {
    const currentDiff = colorDifference(targetHsl, current.hsl);
    const closestDiff = colorDifference(targetHsl, closest.hsl);
    return currentDiff < closestDiff ? current : closest;
  });

  let name = closestColor.name;

  // Add modifiers based on lightness and saturation
  if (s < 30) name = "Nibi " + name; // Dull
  else if (s > 90) name = "Azayaka " + name; // Vivid

  if (l < 25) name = "Kurai " + name; // Dark
  else if (l < 50) name = "Usui " + name; // Light
  else if (l > 80) name = "Akarui " + name; // Bright

  return name;
};

// ============= PHYSICAL COLOR STANDARD MATCHING =============

const findClosestColor = (targetRgb, colorArray) => {
  if (!colorArray || colorArray.length === 0) {
    return null;
  }

  return colorArray.reduce((closest, current) => {
    const currentRgb = current.rgb || [0, 0, 0];
    const currentDiff = Math.sqrt(
      Math.pow(targetRgb[0] - currentRgb[0], 2) +
      Math.pow(targetRgb[1] - currentRgb[1], 2) +
      Math.pow(targetRgb[2] - currentRgb[2], 2),
    );

    const closestRgb = closest.rgb || [0, 0, 0];
    const closestDiff = Math.sqrt(
      Math.pow(targetRgb[0] - closestRgb[0], 2) +
      Math.pow(targetRgb[1] - closestRgb[1], 2) +
      Math.pow(targetRgb[2] - closestRgb[2], 2),
    );

    return currentDiff < closestDiff ? current : closest;
  });
};

// RAL Colors
export const getClosestRAL = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], ralColors);
  return closestColor ? {
    code: closestColor.ral,
    name: closestColor.name,
  } : { code: "N/A", name: "Unknown" };
};

// HKS Colors
export const getClosestHKS = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], hksColors);
  return closestColor ? {
    code: closestColor.hks,
    name: closestColor.name,
  } : { code: "N/A", name: "Unknown" };
};

// COPIC Colors
export const getClosestCOPIC = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], copicColors);
  return closestColor || { number: "N/A", name: "Unknown", rgb: [0, 0, 0] };
};

// PANTONE Colors
export const getClosestPANTONE = (r, g, b) => {
  const targetHex = rgbToHex(r, g, b);

  if (!pantoneColors || Object.keys(pantoneColors).length === 0) {
    return "Unknown";
  }

  const pantoneEntries = Object.entries(pantoneColors);
  const hexColorDifference = (hex1, hex2) => {
    const rgb1 = hexToRgb(hex1);
    const rgb2 = hexToRgb(hex2);
    return Math.sqrt(
      Math.pow(rgb1[0] - rgb2[0], 2) +
      Math.pow(rgb1[1] - rgb2[1], 2) +
      Math.pow(rgb1[2] - rgb2[2], 2),
    );
  };

  const [pantoneNumber, colorInfo] = pantoneEntries.reduce(
    (closest, [currentNumber, currentInfo]) => {
      const currentHex = currentInfo.hex.startsWith("#")
        ? currentInfo.hex
        : "#" + currentInfo.hex;
      const currentDiff = hexColorDifference(targetHex, currentHex);
      const [, closestInfo] = closest;
      const closestHex = closestInfo.hex.startsWith("#")
        ? closestInfo.hex
        : "#" + closestInfo.hex;
      const closestDiff = hexColorDifference(targetHex, closestHex);
      return currentDiff < closestDiff ? [currentNumber, currentInfo] : closest;
    },
    pantoneEntries[0],
  );

  return `${pantoneNumber} (${colorInfo.name})`;
};

// IKEA Colors
export const getClosestIKEA = (r, g, b) => {
  const targetRgb = [r, g, b];

  if (!ikeaColors || ikeaColors.length === 0) {
    return { code: "N/A", name: "No IKEA colors available" };
  }

  const closestColor = ikeaColors.reduce((closest, current) => {
    const currentRgb = hexToRgb(current.hex);
    const currentDiff = Math.sqrt(
      Math.pow(targetRgb[0] - currentRgb[0], 2) +
      Math.pow(targetRgb[1] - currentRgb[1], 2) +
      Math.pow(targetRgb[2] - currentRgb[2], 2),
    );
    const closestDiff = Math.sqrt(
      Math.pow(targetRgb[0] - hexToRgb(closest.hex)[0], 2) +
      Math.pow(targetRgb[1] - hexToRgb(closest.hex)[1], 2) +
      Math.pow(targetRgb[2] - hexToRgb(closest.hex)[2], 2),
    );
    return currentDiff < closestDiff ? current : closest;
  }, ikeaColors[0]);

  return {
    code: closestColor.name || "Unknown",
    name: closestColor.label || "Unnamed",
  };
};

// PRISMACOLOR Colors
export const getClosestPRISMACOLOR = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], prismacolorColors);
  return closestColor ? {
    number: closestColor.number,
    name: closestColor.name,
    hex: closestColor.hex
  } : { number: "N/A", name: "Unknown", hex: "#000000" };
};

// MATERIAL DESIGN Colors
export const getClosestMATERIALDESIGN = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], materialDesignColors);
  return closestColor ? {
    name: closestColor.name,
    shade: closestColor.shade,
    color: closestColor.color
  } : { name: "Unknown", shade: 500, color: "grey" };
};

// TAILWIND Colors
export const getClosestTAILWIND = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], tailwindColors);
  return closestColor ? {
    name: closestColor.name,
    shade: closestColor.shade,
    color: closestColor.color
  } : { name: "Unknown", shade: 500, color: "gray" };
};

// NCS Colors
export const getClosestNCS = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], ncsColors);
  return closestColor ? {
    ncs: closestColor.ncs,
    name: closestColor.name
  } : { ncs: "N/A", name: "Unknown" };
};

// FEDERAL STANDARD Colors
export const getClosestFEDERALSTANDARD = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], federalStandardColors);
  return closestColor ? {
    fs: closestColor.fs,
    name: closestColor.name
  } : { fs: "N/A", name: "Unknown" };
};

// BRITISH STANDARD Colors
export const getClosestBRITISHSTANDARD = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], britishStandardColors);
  return closestColor ? {
    bs: closestColor.bs,
    name: closestColor.name
  } : { bs: "N/A", name: "Unknown" };
};

// MUNSELL Colors
export const getClosestMUNSELL = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], munsellColors);
  return closestColor ? {
    munsell: closestColor.munsell,
    name: closestColor.name
  } : { munsell: "N/A", name: "Unknown" };
};

// AUTOMOTIVE Colors
export const getClosestAUTOMOTIVE = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], automotiveColors);
  return closestColor ? {
    code: closestColor.code,
    brand: closestColor.brand,
    name: closestColor.name
  } : { code: "N/A", brand: "Unknown", name: "Unknown" };
};

// CRAYOLA Colors
export const getClosestCRAYOLA = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], crayolaColors);
  return closestColor ? {
    name: closestColor.name,
    hex: closestColor.hex
  } : { name: "Unknown", hex: "#000000" };
};

// SHERWIN-WILLIAMS Colors
export const getClosestSHERWINWILLIAMS = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], sherwinWilliamsColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// BENJAMIN MOORE Colors
export const getClosestBENJAMINMOORE = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], benjaminMooreColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// DULUX Colors
export const getClosestDULUX = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], duluxColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// RESENE Colors
export const getClosestRESENE = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], reseneColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// VALSPAR Colors
export const getClosestVALSPAR = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], valsparColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// BEHR Colors
export const getClosestBEHR = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], behrColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// PPG Colors
export const getClosestPPG = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], ppgColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// AUSTRALIAN STANDARD Colors
export const getClosestAUSTRALIANSTANDARD = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], australianStandardColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// PITTSBURGH PAINTS Colors
export const getClosestPITTSBURGHPAINTS = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], pittsburghPaintsColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// GLIDDEN Colors
export const getClosestGLIDDEN = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], gliddenColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// WINSOR & NEWTON Colors
export const getClosestWINSORNEWTON = (r, g, b) => {
  const closestColor = findClosestColor([r, g, b], winsorNewtonColors);
  return closestColor ? {
    code: closestColor.code,
    name: closestColor.name
  } : { code: "N/A", name: "Unknown" };
};

// ============= ADVANCED COLOR PROPERTIES =============

// Color Temperature
export const getColorTemperature = (r, g, b) => {
  const [X, Y, Z] = rgbToXYZ(r, g, b);
  const sum = X + Y + Z;
  if (sum === 0) return "N/A";

  const x = X / sum;
  const y = Y / sum;

  // McCamy's approximation for CCT
  const n = (x - 0.3320) / (0.1858 - y);
  const cct = 449 * n * n * n + 3525 * n * n + 6823.3 * n + 5520.33;

  if (cct < 1000 || cct > 25000) return "N/A";

  return Math.round(cct) + "K";
};

// CSS Named Colors
const cssNamedColors = {
  "aliceblue": "#f0f8ff", "antiquewhite": "#faebd7", "aqua": "#00ffff", "aquamarine": "#7fffd4",
  "azure": "#f0ffff", "beige": "#f5f5dc", "bisque": "#ffe4c4", "black": "#000000",
  "blanchedalmond": "#ffebcd", "blue": "#0000ff", "blueviolet": "#8a2be2", "brown": "#a52a2a",
  "burlywood": "#deb887", "cadetblue": "#5f9ea0", "chartreuse": "#7fff00", "chocolate": "#d2691e",
  "coral": "#ff7f50", "cornflowerblue": "#6495ed", "cornsilk": "#fff8dc", "crimson": "#dc143c",
  "cyan": "#00ffff", "darkblue": "#00008b", "darkcyan": "#008b8b", "darkgoldenrod": "#b8860b",
  "darkgray": "#a9a9a9", "darkgreen": "#006400", "darkkhaki": "#bdb76b", "darkmagenta": "#8b008b",
  "darkolivegreen": "#556b2f", "darkorange": "#ff8c00", "darkorchid": "#9932cc", "darkred": "#8b0000",
  "darksalmon": "#e9967a", "darkseagreen": "#8fbc8f", "darkslateblue": "#483d8b", "darkslategray": "#2f4f4f",
  "darkturquoise": "#00ced1", "darkviolet": "#9400d3", "deeppink": "#ff1493", "deepskyblue": "#00bfff",
  "dimgray": "#696969", "dodgerblue": "#1e90ff", "firebrick": "#b22222", "floralwhite": "#fffaf0",
  "forestgreen": "#228b22", "fuchsia": "#ff00ff", "gainsboro": "#dcdcdc", "ghostwhite": "#f8f8ff",
  "gold": "#ffd700", "goldenrod": "#daa520", "gray": "#808080", "green": "#008000",
  "greenyellow": "#adff2f", "honeydew": "#f0fff0", "hotpink": "#ff69b4", "indianred": "#cd5c5c",
  "indigo": "#4b0082", "ivory": "#fffff0", "khaki": "#f0e68c", "lavender": "#e6e6fa",
  "lavenderblush": "#fff0f5", "lawngreen": "#7cfc00", "lemonchiffon": "#fffacd", "lightblue": "#add8e6",
  "lightcoral": "#f08080", "lightcyan": "#e0ffff", "lightgoldenrodyellow": "#fafad2", "lightgray": "#d3d3d3",
  "lightgreen": "#90ee90", "lightpink": "#ffb6c1", "lightsalmon": "#ffa07a", "lightseagreen": "#20b2aa",
  "lightskyblue": "#87cefa", "lightslategray": "#778899", "lightsteelblue": "#b0c4de", "lightyellow": "#ffffe0",
  "lime": "#00ff00", "limegreen": "#32cd32", "linen": "#faf0e6", "magenta": "#ff00ff",
  "maroon": "#800000", "mediumaquamarine": "#66cdaa", "mediumblue": "#0000cd", "mediumorchid": "#ba55d3",
  "mediumpurple": "#9370db", "mediumseagreen": "#3cb371", "mediumslateblue": "#7b68ee", "mediumspringgreen": "#00fa9a",
  "mediumturquoise": "#48d1cc", "mediumvioletred": "#c71585", "midnightblue": "#191970", "mintcream": "#f5fffa",
  "mistyrose": "#ffe4e1", "moccasin": "#ffe4b5", "navajowhite": "#ffdead", "navy": "#000080",
  "oldlace": "#fdf5e6", "olive": "#808000", "olivedrab": "#6b8e23", "orange": "#ffa500",
  "orangered": "#ff4500", "orchid": "#da70d6", "palegoldenrod": "#eee8aa", "palegreen": "#98fb98",
  "paleturquoise": "#afeeee", "palevioletred": "#db7093", "papayawhip": "#ffefd5", "peachpuff": "#ffdab9",
  "peru": "#cd853f", "pink": "#ffc0cb", "plum": "#dda0dd", "powderblue": "#b0e0e6",
  "purple": "#800080", "red": "#ff0000", "rosybrown": "#bc8f8f", "royalblue": "#4169e1",
  "saddlebrown": "#8b4513", "salmon": "#fa8072", "sandybrown": "#f4a460", "seagreen": "#2e8b57",
  "seashell": "#fff5ee", "sienna": "#a0522d", "silver": "#c0c0c0", "skyblue": "#87ceeb",
  "slateblue": "#6a5acd", "slategray": "#708090", "snow": "#fffafa", "springgreen": "#00ff7f",
  "steelblue": "#4682b4", "tan": "#d2b48c", "teal": "#008080", "thistle": "#d8bfd8",
  "tomato": "#ff6347", "turquoise": "#40e0d0", "violet": "#ee82ee", "wheat": "#f5deb3",
  "white": "#ffffff", "whitesmoke": "#f5f5f5", "yellow": "#ffff00", "yellowgreen": "#9acd32"
};

export const getClosestCSSColor = (hex) => {
  const targetRgb = hexToRgb(hex);
  let closestColor = "black";
  let smallestDiff = Infinity;

  Object.entries(cssNamedColors).forEach(([name, colorHex]) => {
    const colorRgb = hexToRgb(colorHex);
    const diff = Math.sqrt(
      Math.pow(targetRgb[0] - colorRgb[0], 2) +
      Math.pow(targetRgb[1] - colorRgb[1], 2) +
      Math.pow(targetRgb[2] - colorRgb[2], 2)
    );
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestColor = name;
    }
  });

  return closestColor;
};

// Dominant Wavelength
export const getDominantWavelength = (r, g, b) => {
  const [X, Y, Z] = rgbToXYZ(r, g, b);
  const sum = X + Y + Z;
  if (sum === 0) return "N/A";

  const x = X / sum;
  const y = Y / sum;

  if (y < 0.05 || x < 0.1 || x > 0.8) return "Non-spectral";

  // Simplified wavelength calculation
  const wavelength = 380 + (780 - 380) * ((x - 0.1667) / (0.7347 - 0.1667));

  if (wavelength < 380 || wavelength > 780) return "Non-spectral";

  return Math.round(wavelength) + "nm";
};

// ============= ACCESSIBILITY FUNCTIONS =============

// WCAG Contrast Ratio
export const getWCAGContrastRatio = (rgb1, rgb2) => {
  const l1 = getRelativeLuminance(rgb1[0], rgb1[1], rgb1[2]);
  const l2 = getRelativeLuminance(rgb2[0], rgb2[1], rgb2[2]);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

// WCAG Compliance Level
export const getWCAGCompliance = (contrastRatio) => {
  return {
    aaaNormal: contrastRatio >= 7.0,
    aaaNormalText: contrastRatio >= 7.0 ? "✓ AAA" : "✗ AAA",
    aaNormal: contrastRatio >= 4.5,
    aaNormalText: contrastRatio >= 4.5 ? "✓ AA" : "✗ AA",
    aaaLarge: contrastRatio >= 4.5,
    aaaLargeText: contrastRatio >= 4.5 ? "✓ AAA Large" : "✗ AAA Large",
    aaLarge: contrastRatio >= 3.0,
    aaLargeText: contrastRatio >= 3.0 ? "✓ AA Large" : "✗ AA Large",
    ratio: contrastRatio.toFixed(2)
  };
};

// Generate Accessible Alternatives
export const getAccessibleAlternatives = (baseHex, backgroundHex = "#FFFFFF") => {
  const baseRgb = hexToRgb(baseHex);
  const bgRgb = hexToRgb(backgroundHex);
  const [h, s, l] = hexToHsl(baseHex);

  const alternatives = [];

  // Try darker versions
  for (let newL = Math.max(0, l - 10); newL >= 0; newL -= 10) {
    const newHex = hslToHex(h, s, newL);
    const newRgb = hexToRgb(newHex);
    const ratio = getWCAGContrastRatio(newRgb, bgRgb);

    if (ratio >= 4.5) {
      alternatives.push({
        hex: newHex,
        ratio: ratio.toFixed(2),
        level: ratio >= 7.0 ? "AAA" : "AA",
        lightness: newL
      });
      break;
    }
  }

  // Try lighter versions  
  for (let newL = Math.min(100, l + 10); newL <= 100; newL += 10) {
    const newHex = hslToHex(h, s, newL);
    const newRgb = hexToRgb(newHex);
    const ratio = getWCAGContrastRatio(newRgb, bgRgb);

    if (ratio >= 4.5) {
      alternatives.push({
        hex: newHex,
        ratio: ratio.toFixed(2),
        level: ratio >= 7.0 ? "AAA" : "AA",
        lightness: newL
      });
      break;
    }
  }

  return alternatives;
};

// Color Blindness Simulation
export const simulateColorBlindness = (hex, type) => {
  const rgb = hexToRgb(hex);
  let [r, g, b] = rgb.map(c => c / 255);

  const matrices = {
    protanopia: [
      [0.567, 0.433, 0],
      [0.558, 0.442, 0],
      [0, 0.242, 0.758]
    ],
    deuteranopia: [
      [0.625, 0.375, 0],
      [0.7, 0.3, 0],
      [0, 0.3, 0.7]
    ],
    tritanopia: [
      [0.95, 0.05, 0],
      [0, 0.433, 0.567],
      [0, 0.475, 0.525]
    ],
    protanomaly: [
      [0.817, 0.183, 0],
      [0.333, 0.667, 0],
      [0, 0.125, 0.875]
    ],
    deuteranomaly: [
      [0.8, 0.2, 0],
      [0.258, 0.742, 0],
      [0, 0.142, 0.858]
    ],
    tritanomaly: [
      [0.967, 0.033, 0],
      [0, 0.733, 0.267],
      [0, 0.183, 0.817]
    ],
    achromatopsia: [
      [0.299, 0.587, 0.114],
      [0.299, 0.587, 0.114],
      [0.299, 0.587, 0.114]
    ]
  };

  const matrix = matrices[type];
  if (!matrix) return hex;

  const newR = Math.round((matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b) * 255);
  const newG = Math.round((matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b) * 255);
  const newB = Math.round((matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b) * 255);

  const toHex = (c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0');

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// ============= ADVANCED COLOR SCIENCE =============

// Delta E CIE76 Color Difference
export const calculateDeltaE76 = (lab1, lab2) => {
  const [l1, a1, b1] = lab1;
  const [l2, a2, b2] = lab2;

  const deltaL = l1 - l2;
  const deltaA = a1 - a2;
  const deltaB = b1 - b2;

  return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
};

// Delta E CIE94 Color Difference
export const calculateDeltaE94 = (lab1, lab2) => {
  const [l1, a1, b1] = lab1;
  const [l2, a2, b2] = lab2;

  const deltaL = l1 - l2;
  const deltaA = a1 - a2;
  const deltaB = b1 - b2;

  const c1 = Math.sqrt(a1 * a1 + b1 * b1);
  const c2 = Math.sqrt(a2 * a2 + b2 * b2);
  const deltaC = c1 - c2;

  const deltaH = Math.sqrt(deltaA * deltaA + deltaB * deltaB - deltaC * deltaC);

  const sl = 1;
  const kc = 1;
  const kh = 1;
  const k1 = 0.045;
  const k2 = 0.015;

  const sc = 1 + k1 * c1;
  const sh = 1 + k2 * c1;

  return Math.sqrt(
    (deltaL / sl) ** 2 +
    (deltaC / (kc * sc)) ** 2 +
    (deltaH / (kh * sh)) ** 2
  );
};

// ============= COLOR HARMONIES AND PSYCHOLOGY =============

// Color Harmonies
export const getColorHarmonies = (baseHex) => {
  const [h, s, l] = hexToHsl(baseHex);

  return {
    monochromatic: [
      baseHex,
      hslToHex(h, s, Math.max(10, l - 20)),
      hslToHex(h, s, Math.max(5, l - 40)),
      hslToHex(h, s, Math.min(95, l + 20)),
      hslToHex(h, s, Math.min(100, l + 40))
    ],
    analogous: [
      baseHex,
      hslToHex((h + 30) % 360, s, l),
      hslToHex((h - 30 + 360) % 360, s, l),
      hslToHex((h + 60) % 360, s, l),
      hslToHex((h - 60 + 360) % 360, s, l)
    ],
    complementary: [
      baseHex,
      hslToHex((h + 180) % 360, s, l)
    ],
    triadic: [
      baseHex,
      hslToHex((h + 120) % 360, s, l),
      hslToHex((h + 240) % 360, s, l)
    ],
    tetradic: [
      baseHex,
      hslToHex((h + 90) % 360, s, l),
      hslToHex((h + 180) % 360, s, l),
      hslToHex((h + 270) % 360, s, l)
    ],
    splitComplementary: [
      baseHex,
      hslToHex((h + 150) % 360, s, l),
      hslToHex((h + 210) % 360, s, l)
    ]
  };
};

// Perceived Brightness
export const getPerceivedBrightness = (r, g, b) => {
  return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
};

// Color Temperature Type
export const getColorTemperatureType = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  if ((h >= 0 && h <= 60) || (h >= 300 && h <= 360)) {
    return {
      type: "warm",
      description: "Warm colors evoke feelings of energy, passion, and comfort"
    };
  } else {
    return {
      type: "cool",
      description: "Cool colors evoke feelings of calm, tranquility, and professionalism"
    };
  }
};

// Color Psychology
export const getColorPsychology = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let primaryColor = "";
  let associations = [];

  if (h >= 345 || h < 15) {
    primaryColor = "Red";
    associations = ["passion", "energy", "danger", "love", "power", "excitement"];
  } else if (h >= 15 && h < 45) {
    primaryColor = "Orange";
    associations = ["creativity", "enthusiasm", "warmth", "adventure", "confidence", "playfulness"];
  } else if (h >= 45 && h < 75) {
    primaryColor = "Yellow";
    associations = ["happiness", "optimism", "intellect", "energy", "cheerfulness", "caution"];
  } else if (h >= 75 && h < 165) {
    primaryColor = "Green";
    associations = ["nature", "growth", "harmony", "freshness", "peace", "balance"];
  } else if (h >= 165 && h < 255) {
    primaryColor = "Blue";
    associations = ["trust", "loyalty", "wisdom", "confidence", "intelligence", "stability"];
  } else if (h >= 255 && h < 285) {
    primaryColor = "Purple";
    associations = ["luxury", "mystery", "magic", "creativity", "wisdom", "spirituality"];
  } else {
    primaryColor = "Pink/Magenta";
    associations = ["romance", "femininity", "tenderness", "compassion", "playfulness", "charm"];
  }

  // Adjust for saturation and lightness
  if (s < 20) {
    associations.push("neutral", "calm", "professional");
  } else if (s > 80) {
    associations.push("vibrant", "energetic", "bold");
  }

  if (l < 20) {
    associations.push("sophisticated", "formal", "mysterious");
  } else if (l > 80) {
    associations.push("soft", "gentle", "peaceful");
  }

  return {
    primaryColor,
    associations: associations.slice(0, 6),
    saturationLevel: s < 20 ? "low" : s > 80 ? "high" : "medium",
    lightnessLevel: l < 30 ? "dark" : l > 70 ? "light" : "medium"
  };
};

// ============= ENHANCED CULTURAL & CONCEPTUAL DATA =============

// Cultural Color Meanings (cross-cultural analysis)
export const getColorCulturalMeanings = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let meanings = {
    western: [],
    eastern: [],
    general: []
  };

  if (h >= 345 || h < 15) {
    // Red
    meanings.western = ["passion", "danger", "love", "power"];
    meanings.eastern = ["luck", "celebration", "prosperity", "joy"];
    meanings.general = ["energy", "excitement", "attention"];
  } else if (h >= 15 && h < 45) {
    // Orange
    meanings.western = ["enthusiasm", "creativity", "autumn", "warmth"];
    meanings.eastern = ["spirituality", "transformation", "sacred"];
    meanings.general = ["vitality", "sociability", "confidence"];
  } else if (h >= 45 && h < 75) {
    // Yellow
    meanings.western = ["optimism", "happiness", "caution", "intellect"];
    meanings.eastern = ["royalty", "sacred", "prosperity"];
    meanings.general = ["enlightenment", "energy", "warmth"];
  } else if (h >= 75 && h < 165) {
    // Green
    meanings.western = ["nature", "growth", "money", "freshness"];
    meanings.eastern = ["fertility", "harmony", "rebirth"];
    meanings.general = ["balance", "peace", "health"];
  } else if (h >= 165 && h < 255) {
    // Blue
    meanings.western = ["trust", "corporate", "calm", "sadness"];
    meanings.eastern = ["immortality", "healing", "spirituality"];
    meanings.general = ["wisdom", "depth", "stability"];
  } else if (h >= 255 && h < 285) {
    // Purple
    meanings.western = ["royalty", "luxury", "spirituality"];
    meanings.eastern = ["nobility", "wealth", "enlightenment"];
    meanings.general = ["mystery", "magic", "creativity"];
  } else {
    // Pink/Magenta
    meanings.western = ["femininity", "romance", "youth"];
    meanings.eastern = ["marriage", "spring", "good fortune"];
    meanings.general = ["tenderness", "playfulness", "compassion"];
  }

  return meanings;
};

// Natural Associations
export const getColorNaturalAssociations = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let associations = [];

  if (h >= 345 || h < 15) {
    // Red
    if (l > 60) associations = ["rose petals", "cherry blossoms", "sunset", "coral"];
    else if (l > 30) associations = ["poppies", "strawberries", "cardinals", "autumn leaves"];
    else associations = ["wine", "dark wood", "evening sky", "blood"];
  } else if (h >= 15 && h < 45) {
    // Orange
    if (l > 60) associations = ["peach", "apricot", "dawn", "sand"];
    else if (l > 30) associations = ["pumpkin", "oranges", "autumn", "fire"];
    else associations = ["rust", "clay", "earth", "tiger fur"];
  } else if (h >= 45 && h < 75) {
    // Yellow
    if (l > 60) associations = ["sunlight", "butter", "daffodils", "lemon"];
    else if (l > 30) associations = ["gold", "sunflowers", "corn", "honey"];
    else associations = ["amber", "brass", "mustard", "ochre soil"];
  } else if (h >= 75 && h < 165) {
    // Green
    if (l > 60) associations = ["mint", "spring leaves", "sea foam", "jade"];
    else if (l > 30) associations = ["grass", "emerald", "pine", "moss"];
    else associations = ["forest", "ivy", "hunter green", "deep sea"];
  } else if (h >= 165 && h < 255) {
    // Blue
    if (l > 60) associations = ["sky", "ice", "pale blue flowers", "mist"];
    else if (l > 30) associations = ["ocean", "sapphire", "bluebird", "clear water"];
    else associations = ["midnight", "deep ocean", "navy", "storm clouds"];
  } else if (h >= 255 && h < 285) {
    // Purple
    if (l > 60) associations = ["lavender", "lilac", "amethyst", "twilight"];
    else if (l > 30) associations = ["grapes", "plum", "iris", "eggplant"];
    else associations = ["deep plum", "nightshade", "blackberry", "aubergine"];
  } else {
    // Pink/Magenta
    if (l > 60) associations = ["cherry blossoms", "cotton candy", "roses", "sunrise"];
    else if (l > 30) associations = ["fuchsia flowers", "watermelon", "flamingo", "coral reef"];
    else associations = ["deep rose", "raspberry", "magenta flowers", "wine"];
  }

  return associations.slice(0, 4);
};

// Symbolic Meanings
export const getColorSymbolicMeanings = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let symbols = [];

  if (h >= 345 || h < 15) {
    symbols = ["passion", "danger", "power", "love", "courage", "war"];
  } else if (h >= 15 && h < 45) {
    symbols = ["enthusiasm", "success", "encouragement", "stimulation", "attraction", "creativity"];
  } else if (h >= 45 && h < 75) {
    symbols = ["sunshine", "hope", "happiness", "intellect", "warning", "clarity"];
  } else if (h >= 75 && h < 165) {
    symbols = ["growth", "harmony", "safety", "fertility", "money", "envy"];
  } else if (h >= 165 && h < 255) {
    symbols = ["trust", "loyalty", "wisdom", "confidence", "faith", "intelligence"];
  } else if (h >= 255 && h < 285) {
    symbols = ["royalty", "luxury", "ambition", "mystery", "magic", "spirituality"];
  } else {
    symbols = ["romance", "femininity", "compassion", "nurturing", "playfulness", "delicacy"];
  }

  // Add lightness-based symbols
  if (l < 25) {
    symbols.push("elegance", "formality", "sophistication");
  } else if (l > 75) {
    symbols.push("purity", "innocence", "cleanliness");
  }

  return symbols.slice(0, 6);
};

// Industry Usage Patterns
export const getColorIndustryUsage = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let industries = [];

  if (h >= 345 || h < 15) {
    // Red
    if (s > 60) industries = ["Food & Beverage", "Entertainment", "Energy Drinks", "Fast Food", "Automotive (sports)", "Emergency Services"];
    else industries = ["Wine & Spirits", "Fashion", "Cosmetics"];
  } else if (h >= 15 && h < 45) {
    // Orange
    industries = ["E-commerce", "Creative Agencies", "Children's Products", "Sports Teams", "Construction", "Tech Startups"];
  } else if (h >= 45 && h < 75) {
    // Yellow
    if (l > 60) industries = ["Optimism Brands", "Children's Education", "Food Services", "Energy Companies"];
    else industries = ["Caution Signs", "Construction", "Transportation"];
  } else if (h >= 75 && h < 165) {
    // Green
    if (s > 50) industries = ["Environmental", "Organic Food", "Health & Wellness", "Financial Services", "Sustainability"];
    else industries = ["Military", "Outdoor Gear", "Agriculture"];
  } else if (h >= 165 && h < 255) {
    // Blue
    if (l > 60) industries = ["Healthcare", "Pharmaceuticals", "Wellness", "Water Products"];
    else if (l > 30) industries = ["Technology", "Finance", "Corporate", "Social Media", "Airlines", "Insurance"];
    else industries = ["Law Enforcement", "Government", "Professional Services"];
  } else if (h >= 255 && h < 285) {
    // Purple
    industries = ["Luxury Brands", "Cosmetics", "Spirituality", "Creative Industries", "Innovation"];
  } else {
    // Pink/Magenta
    industries = ["Beauty & Cosmetics", "Fashion", "Childcare", "Bakeries", "Event Planning", "Feminine Products"];
  }

  return industries.slice(0, 5);
};

// Seasonal Color Analysis
export const getColorSeasonalPalette = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let season = "";
  let description = "";
  let characteristics = [];

  // Warm vs Cool undertone
  const isWarm = (h >= 0 && h <= 60) || (h >= 300 && h <= 360);

  // Light vs Dark
  const isLight = l > 60;

  // Muted vs Clear
  const isMuted = s < 50;

  if (isWarm && isLight && !isMuted) {
    season = "Spring";
    description = "Warm, clear, and light colors with yellow undertones";
    characteristics = ["fresh", "youthful", "energetic", "bright"];
  } else if (!isWarm && isLight && isMuted) {
    season = "Summer";
    description = "Cool, soft, and muted colors with blue undertones";
    characteristics = ["elegant", "romantic", "gentle", "refined"];
  } else if (isWarm && !isLight && !isMuted) {
    season = "Autumn";
    description = "Warm, rich, and muted colors with golden undertones";
    characteristics = ["earthy", "warm", "rich", "sophisticated"];
  } else if (!isWarm && !isLight) {
    season = "Winter";
    description = "Cool, clear, and contrasting colors with blue undertones";
    characteristics = ["dramatic", "bold", "elegant", "striking"];
  } else {
    // Neutral/transitional
    season = "Neutral";
    description = "Versatile color that works across seasons";
    characteristics = ["adaptable", "balanced", "timeless", "universal"];
  }

  return {
    season,
    description,
    characteristics,
    undertone: isWarm ? "warm" : "cool",
    intensity: isMuted ? "muted" : "clear",
    value: isLight ? "light" : "deep"
  };
};

// Feng Shui Color Meanings and Placement
export const getColorFengShui = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let element = "";
  let energy = "";
  let rooms = [];
  let direction = "";
  let meaning = "";

  if (h >= 345 || h < 15) {
    // Red
    element = "Fire";
    energy = "Yang (active, passionate)";
    rooms = ["dining room", "living room", "home office (accent)"];
    direction = "South";
    meaning = "Recognition, fame, passion, vitality";
  } else if (h >= 15 && h < 45) {
    // Orange
    element = "Fire";
    energy = "Yang (social, joyful)";
    rooms = ["kitchen", "dining room", "playroom"];
    direction = "South";
    meaning = "Social interaction, happiness, conversation";
  } else if (h >= 45 && h < 75) {
    // Yellow
    element = "Earth";
    energy = "Yang (cheerful, optimistic)";
    rooms = ["kitchen", "dining room", "children's room"];
    direction = "Center";
    meaning = "Cheerfulness, mental clarity, optimism";
  } else if (h >= 75 && h < 165) {
    // Green
    element = "Wood";
    energy = "Yang (growth, renewal)";
    rooms = ["bedroom", "bathroom", "meditation room", "office"];
    direction = "East";
    meaning = "Growth, healing, new beginnings, abundance";
  } else if (h >= 165 && h < 255) {
    // Blue
    element = "Water";
    energy = "Yin (calm, peaceful)";
    rooms = ["bedroom", "bathroom", "study"];
    direction = "North";
    meaning = "Calmness, wisdom, spirituality, career success";
  } else if (h >= 255 && h < 285) {
    // Purple
    element = "Fire";
    energy = "Yin (spiritual, mysterious)";
    rooms = ["meditation room", "bedroom", "spiritual space"];
    direction = "South";
    meaning = "Spiritual awareness, luxury, nobility, imagination";
  } else {
    // Pink/Magenta
    element = "Fire";
    energy = "Yin (nurturing, loving)";
    rooms = ["bedroom", "living room", "children's room"];
    direction = "Southwest";
    meaning = "Love, romance, relationships, tenderness";
  }

  // Add neutral colors consideration
  if (s < 20) {
    element = "Metal";
    energy = "Balanced (neutral, sophisticated)";
    rooms = ["any room (neutral base)"];
    direction = "West/Northwest";
    meaning = "Clarity, precision, efficiency, focus";
  }

  return {
    element,
    energy,
    rooms: rooms.slice(0, 3),
    direction,
    meaning
  };
};

// Chakra Energy Associations
export const getColorChakraAssociation = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let chakra = "";
  let sanskrit = "";
  let location = "";
  let meaning = "";
  let qualities = [];

  if (h >= 345 || h < 15) {
    // Red
    chakra = "Root Chakra";
    sanskrit = "Muladhara";
    location = "Base of spine";
    meaning = "Grounding, survival, security";
    qualities = ["stability", "courage", "physical energy", "security"];
  } else if (h >= 15 && h < 45) {
    // Orange
    chakra = "Sacral Chakra";
    sanskrit = "Svadhisthana";
    location = "Lower abdomen";
    meaning = "Creativity, sexuality, emotions";
    qualities = ["creativity", "pleasure", "passion", "emotional balance"];
  } else if (h >= 45 && h < 75) {
    // Yellow
    chakra = "Solar Plexus Chakra";
    sanskrit = "Manipura";
    location = "Upper abdomen";
    meaning = "Personal power, confidence, will";
    qualities = ["confidence", "self-esteem", "willpower", "transformation"];
  } else if (h >= 75 && h < 135) {
    // Green
    chakra = "Heart Chakra";
    sanskrit = "Anahata";
    location = "Center of chest";
    meaning = "Love, compassion, healing";
    qualities = ["love", "compassion", "forgiveness", "harmony"];
  } else if (h >= 165 && h < 195) {
    // Light Blue/Cyan
    chakra = "Throat Chakra";
    sanskrit = "Vishuddha";
    location = "Throat";
    meaning = "Communication, expression, truth";
    qualities = ["communication", "truth", "expression", "clarity"];
  } else if (h >= 195 && h < 255) {
    // Blue/Indigo
    chakra = "Third Eye Chakra";
    sanskrit = "Ajna";
    location = "Forehead (between eyes)";
    meaning = "Intuition, wisdom, imagination";
    qualities = ["intuition", "insight", "wisdom", "perception"];
  } else if (h >= 255 && h < 285) {
    // Purple/Violet
    chakra = "Crown Chakra";
    sanskrit = "Sahasrara";
    location = "Top of head";
    meaning = "Spiritual connection, enlightenment";
    qualities = ["spirituality", "enlightenment", "consciousness", "bliss"];
  } else {
    // Pink
    chakra = "Heart Chakra (Higher Aspect)";
    sanskrit = "Anahata";
    location = "Center of chest";
    meaning = "Unconditional love, compassion";
    qualities = ["unconditional love", "compassion", "nurturing", "healing"];
  }

  return {
    chakra,
    sanskrit,
    location,
    meaning,
    qualities: qualities.slice(0, 4)
  };
};

// Gemstone and Crystal Associations
export const getColorGemstoneAssociations = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let gemstones = [];

  if (h >= 345 || h < 15) {
    // Red
    if (l > 60) gemstones = ["Rose Quartz (pale)", "Pink Tourmaline", "Coral", "Rhodochrosite"];
    else if (l > 30) gemstones = ["Ruby", "Garnet", "Red Jasper", "Carnelian"];
    else gemstones = ["Garnet (dark)", "Bloodstone", "Red Tiger's Eye"];
  } else if (h >= 15 && h < 45) {
    // Orange
    if (l > 60) gemstones = ["Orange Calcite", "Peach Moonstone", "Sunstone (pale)"];
    else if (l > 30) gemstones = ["Carnelian", "Orange Citrine", "Sunstone", "Fire Opal"];
    else gemstones = ["Tiger's Eye", "Amber (dark)", "Hessonite Garnet"];
  } else if (h >= 45 && h < 75) {
    // Yellow
    if (l > 60) gemstones = ["Yellow Calcite", "Lemon Quartz", "Golden Beryl"];
    else if (l > 30) gemstones = ["Citrine", "Yellow Jasper", "Golden Topaz", "Pyrite"];
    else gemstones = ["Tiger's Eye", "Yellow Jasper (dark)", "Bronze"];
  } else if (h >= 75 && h < 165) {
    // Green
    if (l > 60) gemstones = ["Green Aventurine", "Prehnite", "Chrysoprase", "Peridot"];
    else if (l > 30) gemstones = ["Emerald", "Jade", "Malachite", "Green Tourmaline"];
    else gemstones = ["Moss Agate", "Dark Jade", "Serpentine", "Green Jasper"];
  } else if (h >= 165 && h < 255) {
    // Blue
    if (l > 60) gemstones = ["Aquamarine", "Blue Lace Agate", "Celestite", "Angelite"];
    else if (l > 30) gemstones = ["Sapphire", "Lapis Lazuli", "Sodalite", "Turquoise"];
    else gemstones = ["Azurite", "Kyanite", "Blue Tiger's Eye", "Iolite"];
  } else if (h >= 255 && h < 285) {
    // Purple
    if (l > 60) gemstones = ["Amethyst (light)", "Lepidolite", "Kunzite", "Purple Fluorite"];
    else if (l > 30) gemstones = ["Amethyst", "Purple Sapphire", "Charoite", "Sugilite"];
    else gemstones = ["Amethyst (dark)", "Purple Garnet", "Iolite (purple)"];
  } else {
    // Pink/Magenta
    if (l > 60) gemstones = ["Rose Quartz", "Pink Opal", "Morganite", "Pink Chalcedony"];
    else if (l > 30) gemstones = ["Pink Tourmaline", "Rhodonite", "Pink Sapphire", "Rubellite"];
    else gemstones = ["Rhodolite Garnet", "Pink Spinel", "Pink Tourmaline (deep)"];
  }

  // Add clear/white stones for very light colors
  if (l > 90) {
    gemstones = ["Clear Quartz", "Diamond", "Moonstone", "Selenite"];
  }

  // Add black/dark stones for very dark colors
  if (l < 10) {
    gemstones = ["Black Onyx", "Black Tourmaline", "Obsidian", "Hematite"];
  }

  return gemstones.slice(0, 4);
};

// Food Associations
export const getColorFoodAssociations = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let foods = [];

  if (h >= 345 || h < 15) {
    // Red
    if (l > 60) foods = ["strawberries", "watermelon", "raspberries", "cherries", "red bell pepper"];
    else if (l > 30) foods = ["tomatoes", "red apples", "cranberries", "pomegranate", "red grapes"];
    else foods = ["beets", "red wine", "dark cherries", "red cabbage"];
  } else if (h >= 15 && h < 45) {
    // Orange
    if (l > 60) foods = ["peaches", "apricots", "cantaloupe", "papaya"];
    else if (l > 30) foods = ["oranges", "carrots", "pumpkin", "sweet potato", "tangerines"];
    else foods = ["butternut squash", "yams", "persimmons"];
  } else if (h >= 45 && h < 75) {
    // Yellow
    if (l > 60) foods = ["bananas", "pineapple", "corn", "lemons"];
    else if (l > 30) foods = ["golden delicious apples", "honey", "yellow bell pepper", "mango"];
    else foods = ["mustard", "saffron", "curry", "turmeric"];
  } else if (h >= 75 && h < 165) {
    // Green
    if (l > 60) foods = ["lettuce", "celery", "green grapes", "honeydew melon", "cucumber"];
    else if (l > 30) foods = ["broccoli", "green apples", "kiwi", "limes", "avocado"];
    else foods = ["kale", "spinach", "basil", "green tea", "brussels sprouts"];
  } else if (h >= 165 && h < 255) {
    // Blue
    if (l > 60) foods = ["blue corn", "blue potatoes (light)", "blue cheese (mild)"];
    else if (l > 30) foods = ["blueberries", "blue potatoes", "purple cabbage (bluish)"];
    else foods = ["blackberries", "black currants", "dark purple grapes"];
  } else if (h >= 255 && h < 285) {
    // Purple
    if (l > 60) foods = ["lavender", "purple cauliflower", "taro"];
    else if (l > 30) foods = ["eggplant", "plums", "purple grapes", "acai berries", "figs"];
    else foods = ["blackberries", "dark plums", "purple sweet potato"];
  } else {
    // Pink/Magenta
    if (l > 60) foods = ["pink grapefruit", "dragon fruit", "cotton candy", "strawberry ice cream"];
    else if (l > 30) foods = ["salmon", "pink shrimp", "guava", "watermelon (pink)"];
    else foods = ["beets", "pomegranate", "red onion (pink inside)"];
  }

  // Special cases
  if (s < 15) {
    // Neutral/gray tones
    foods = ["mushrooms", "oysters", "salt", "white rice", "tofu"];
  }

  return foods.slice(0, 5);
};

// Historical Period Styling
export const getColorHistoricalPeriod = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let periods = [];

  // Determine if it's muted, bold, or neutral
  const isMuted = s < 40;
  const isBold = s > 70;
  const isLight = l > 70;
  const isDark = l < 30;

  if (isMuted && isLight) {
    periods = ["Victorian Era", "French Provincial", "Shabby Chic", "Coastal"];
  } else if (isMuted && !isLight && !isDark) {
    periods = ["Arts & Crafts", "Scandinavian Modern", "Japanese Zen", "Mid-Century Modern"];
  } else if (isBold && (h >= 345 || h < 75)) {
    // Bold warm colors
    periods = ["Art Deco", "Retro 1950s", "1970s Bohemian", "Maximalist"];
  } else if (isBold && h >= 165 && h < 255) {
    // Bold blue
    periods = ["Art Deco", "Hollywood Regency", "Nautical", "Mediterranean"];
  } else if (h >= 75 && h < 165 && isBold) {
    // Bold green
    periods = ["Tropical Colonial", "Art Nouveau", "Bohemian", "1970s Retro"];
  } else if (h >= 255 && isBold) {
    // Bold purple/pink
    periods = ["Art Deco", "1960s Mod", "Rococo", "Hollywood Regency"];
  } else if (isDark && isMuted) {
    periods = ["Industrial", "Gothic Victorian", "Traditional English", "Craftsman"];
  } else if (s < 15 && l > 85) {
    // Very light neutral
    periods = ["Minimalist", "Scandinavian", "Modern Contemporary", "Coastal"];
  } else if (s < 15 && l < 20) {
    // Very dark neutral
    periods = ["Industrial", "Modern Contemporary", "Gothic", "Art Deco"];
  } else {
    periods = ["Contemporary", "Transitional", "Eclectic", "Modern"];
  }

  return periods.slice(0, 4);
};

// Marketing Psychology Metrics
export const getColorMarketingPsychology = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let callToAction = "";
  let conversionImpact = "";
  let brandPersonality = [];
  let targetAudience = "";
  let avoidUsing = "";

  if (h >= 345 || h < 15) {
    // Red
    callToAction = "High urgency - creates excitement and drives immediate action";
    conversionImpact = "Increases urgency (sale signs, clearance, CTA buttons)";
    brandPersonality = ["bold", "energetic", "passionate", "youthful"];
    targetAudience = "Impulse buyers, young audiences, thrill-seekers";
    avoidUsing = "Conservative financial services, healthcare (anxiety trigger)";
  } else if (h >= 15 && h < 45) {
    // Orange
    callToAction = "Friendly and approachable - encourages interaction";
    conversionImpact = "Positive response for subscriptions and friendly CTAs";
    brandPersonality = ["friendly", "confident", "creative", "adventurous"];
    targetAudience = "Young professionals, creative types, tech-savvy users";
    avoidUsing = "Luxury brands, formal services, law firms";
  } else if (h >= 45 && h < 75) {
    // Yellow
    callToAction = "Attention-grabbing - optimism and clarity";
    conversionImpact = "Grabs attention but use sparingly (can cause eye strain)";
    brandPersonality = ["optimistic", "clear", "youthful", "innovative"];
    targetAudience = "Budget-conscious shoppers, families, children";
    avoidUsing = "Luxury brands (cheapens perception), small text";
  } else if (h >= 75 && h < 165) {
    // Green
    callToAction = "Reassuring and affirmative - 'go' signal";
    conversionImpact = "High conversion for 'confirm' and positive actions";
    brandPersonality = ["natural", "trustworthy", "growth-oriented", "peaceful"];
    targetAudience = "Eco-conscious, health-focused, financial growth seekers";
    avoidUsing = "Fast food (unless health-focused), entertainment";
  } else if (h >= 165 && h < 255) {
    // Blue
    callToAction = "Trust-building - creates sense of security";
    conversionImpact = "Highest trust factor - ideal for banks, social media";
    brandPersonality = ["trustworthy", "professional", "reliable", "calm"];
    targetAudience = "Corporate, professionals, older demographics, security-conscious";
    avoidUsing = "Food industry (suppresses appetite), impulsive purchases";
  } else if (h >= 255 && h < 285) {
    // Purple
    callToAction = "Premium and exclusive - aspirational";
    conversionImpact = "Effective for luxury and premium offerings";
    brandPersonality = ["luxurious", "creative", "wise", "spiritual"];
    targetAudience = "Luxury consumers, creative professionals, spiritual seekers";
    avoidUsing = "Budget brands, everyday products, masculine brands";
  } else {
    // Pink/Magenta
    callToAction = "Gentle and inviting - nurturing response";
    conversionImpact = "Effective for beauty, fashion, feminine products";
    brandPersonality = ["compassionate", "playful", "modern", "youthful"];
    targetAudience = "Female audiences, young consumers, beauty/fashion shoppers";
    avoidUsing = "Masculine brands, heavy industry, serious financial services";
  }

  return {
    callToAction,
    conversionImpact,
    brandPersonality: brandPersonality.slice(0, 4),
    targetAudience,
    avoidUsing
  };
};

// Regional Design Preferences
export const getColorRegionalPreferences = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let regions = [];

  // Scandinavian: light neutrals, muted blues, whites
  if ((s < 30 && l > 70) || (h >= 165 && h < 255 && s < 40)) {
    regions.push("Scandinavian");
  }

  // Mediterranean: bright blues, terracotta, white, warm earth tones
  if ((h >= 165 && h < 255 && s > 50) || (h >= 15 && h < 45 && l < 60)) {
    regions.push("Mediterranean");
  }

  // Japanese/Zen: muted neutrals, natural greens, soft earth tones
  if ((s < 35 && l > 40 && l < 75) || (h >= 75 && h < 135 && s < 45)) {
    regions.push("Japanese/Zen");
  }

  // Middle Eastern: rich jewel tones, gold, deep reds and blues
  if ((s > 60 && l < 55 && l > 25) && (h >= 255 || h < 75 || (h >= 165 && h < 255))) {
    regions.push("Middle Eastern");
  }

  // Tropical/Caribbean: bright, saturated colors - turquoise, coral, lime
  if (s > 60 && l > 45 && l < 75) {
    regions.push("Tropical/Caribbean");
  }

  // American Southwest: terracotta, turquoise, warm earth tones
  if ((h >= 15 && h < 45 && s > 40) || (h >= 165 && h < 195 && s > 50)) {
    regions.push("American Southwest");
  }

  // French Country: soft pastels, muted blues, lavender, cream
  if ((s < 50 && l > 65) || (h >= 255 && h < 285 && l > 60)) {
    regions.push("French Country");
  }

  // Industrial/Urban: grays, blacks, raw metals, deep charcoals
  if (s < 20 && l < 50) {
    regions.push("Industrial/Urban");
  }

  // English Traditional: deep greens, burgundy, navy, gold
  if (s > 40 && l < 40) {
    regions.push("English Traditional");
  }

  // If no specific match, add contemporary
  if (regions.length === 0) {
    regions.push("Contemporary/Universal");
  }

  return regions.slice(0, 4);
};

// Material and Texture Associations
export const getColorMaterialAssociations = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let materials = [];
  let textures = [];

  // Determine material based on color characteristics
  if (s < 15 && l > 85) {
    // Very light neutral
    materials = ["marble", "porcelain", "linen", "alabaster", "cotton"];
    textures = ["smooth", "polished", "soft", "matte"];
  } else if (s < 15 && l < 20) {
    // Very dark neutral
    materials = ["charcoal", "slate", "iron", "ebony wood", "graphite"];
    textures = ["rough", "matte", "textured", "industrial"];
  } else if (s < 25) {
    // Muted/neutral
    materials = ["concrete", "stone", "wool", "suede", "brushed metal"];
    textures = ["textured", "matte", "organic", "natural"];
  } else if (h >= 45 && h < 75 && s > 50) {
    // Yellow/gold
    materials = ["gold", "brass", "amber", "honey", "satin"];
    textures = ["lustrous", "warm", "metallic", "smooth"];
  } else if (h >= 15 && h < 45) {
    // Orange/warm earth
    materials = ["terracotta", "copper", "leather", "wood", "brick"];
    textures = ["warm", "organic", "aged", "natural"];
  } else if (h >= 345 || h < 15) {
    // Red
    materials = ["velvet", "silk", "lacquer", "enamel", "ceramic"];
    textures = ["luxurious", "glossy", "rich", "smooth"];
  } else if (h >= 75 && h < 165) {
    // Green
    materials = ["jade", "moss", "patina copper", "glass", "foliage"];
    textures = ["organic", "natural", "translucent", "soft"];
  } else if (h >= 165 && h < 255) {
    // Blue
    materials = ["glass", "ceramic", "water", "sky", "denim"];
    textures = ["smooth", "cool", "reflective", "fluid"];
  } else if (h >= 255 && h < 285) {
    // Purple
    materials = ["velvet", "amethyst", "silk", "satin", "plush"];
    textures = ["luxurious", "soft", "rich", "elegant"];
  } else {
    // Pink
    materials = ["rose quartz", "silk", "pearl", "blush fabric", "porcelain"];
    textures = ["delicate", "smooth", "lustrous", "soft"];
  }

  // Adjust for lightness
  if (l > 75) {
    textures.push("airy", "light");
  } else if (l < 25) {
    textures.push("deep", "rich");
  }

  return {
    materials: materials.slice(0, 5),
    textures: textures.slice(0, 4)
  };
};

// Color in Film & Photography - Cinematic Color Grading
export const getColorFilmPhotography = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let cinematicStyle = "";
  let moodBoarding = [];
  let filmStockAssociation = "";
  let gradingTechnique = "";

  const isMuted = s < 40;
  const isVibrant = s > 70;
  const isDark = l < 30;
  const isLight = l > 70;

  if (isMuted && (h >= 165 && h < 195)) {
    // Teal/cyan muted
    cinematicStyle = "Blockbuster / Modern Cinema";
    moodBoarding = ["epic", "dramatic", "dystopian", "sci-fi"];
    filmStockAssociation = "Digital cinema (teal-orange look)";
    gradingTechnique = "Orange-teal color grading";
  } else if (isVibrant && (h >= 15 && h < 45) && l > 40) {
    // Orange vibrant
    cinematicStyle = "Blockbuster / Action";
    moodBoarding = ["explosive", "high-energy", "sunset", "warmth"];
    filmStockAssociation = "Digital cinema (teal-orange look)";
    gradingTechnique = "Warm highlights, cool shadows";
  } else if (isMuted && isLight) {
    // Pastel/washed out
    cinematicStyle = "Indie / Arthouse";
    moodBoarding = ["nostalgic", "dreamy", "melancholic", "introspective"];
    filmStockAssociation = "Faded film stock, vintage";
    gradingTechnique = "Desaturated, lifted blacks";
  } else if (isDark && isMuted) {
    // Dark muted
    cinematicStyle = "Film Noir / Thriller";
    moodBoarding = ["mysterious", "suspenseful", "moody", "atmospheric"];
    filmStockAssociation = "High contrast B&W, deep shadows";
    gradingTechnique = "Crushed blacks, high contrast";
  } else if (isVibrant && (h >= 285 || h < 15)) {
    // Vibrant warm (red/pink/magenta)
    cinematicStyle = "Neon Noir / Cyberpunk";
    moodBoarding = ["futuristic", "electric", "synthetic", "urban"];
    filmStockAssociation = "Neon lighting, digital";
    gradingTechnique = "Saturated neons, high contrast";
  } else if (isVibrant && h >= 75 && h < 165) {
    // Vibrant green
    cinematicStyle = "Matrix / Sci-Fi";
    moodBoarding = ["digital", "synthetic", "otherworldly"];
    filmStockAssociation = "Digital manipulation";
    gradingTechnique = "Green-tinted, high saturation";
  } else if (s < 20) {
    // Monochromatic
    cinematicStyle = "Classic / Film Noir";
    moodBoarding = ["timeless", "elegant", "serious", "dramatic"];
    filmStockAssociation = "Black & white film";
    gradingTechnique = "Monochrome, contrast control";
  } else {
    cinematicStyle = "Natural / Documentary";
    moodBoarding = ["realistic", "authentic", "grounded"];
    filmStockAssociation = "Kodak Vision3, natural color";
    gradingTechnique = "Minimal color correction";
  }

  return {
    cinematicStyle,
    moodBoarding: moodBoarding.slice(0, 4),
    filmStockAssociation,
    gradingTechnique
  };
};

// Music Genre Associations
export const getColorMusicGenre = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let genres = [];
  let vibe = "";

  if (h >= 345 || h < 15) {
    // Red
    if (s > 70) {
      genres = ["Rock", "Punk", "Heavy Metal", "EDM"];
      vibe = "High energy, aggressive, passionate";
    } else {
      genres = ["Jazz", "R&B", "Soul"];
      vibe = "Sultry, romantic, emotional";
    }
  } else if (h >= 15 && h < 45) {
    // Orange
    genres = ["Indie Rock", "Alternative", "Funk", "Reggae"];
    vibe = "Upbeat, creative, groovy";
  } else if (h >= 45 && h < 75) {
    // Yellow
    genres = ["Pop", "Dance", "Disco", "Summer Hits"];
    vibe = "Happy, energetic, feel-good";
  } else if (h >= 75 && h < 165) {
    // Green
    if (l < 40) {
      genres = ["Hip-Hop", "Trap", "Reggae", "Dub"];
      vibe = "Urban, chill, laid-back";
    } else {
      genres = ["Folk", "Acoustic", "Nature Sounds", "Ambient"];
      vibe = "Natural, calming, organic";
    }
  } else if (h >= 165 && h < 255) {
    // Blue
    if (l < 40) {
      genres = ["Blues", "Jazz", "Trip-Hop", "Lo-fi"];
      vibe = "Melancholic, introspective, deep";
    } else {
      genres = ["Chillwave", "Ambient", "Dream Pop", "Classical"];
      vibe = "Ethereal, peaceful, contemplative";
    }
  } else if (h >= 255 && h < 285) {
    // Purple
    genres = ["Synthwave", "Electronic", "Psychedelic Rock", "Dream Pop"];
    vibe = "Mysterious, experimental, futuristic";
  } else {
    // Pink/Magenta
    genres = ["Pop", "K-Pop", "Dance-Pop", "Bubblegum Pop"];
    vibe = "Playful, youthful, upbeat";
  }

  // Adjust for saturation and lightness
  if (s < 20) {
    genres = ["Classical", "Ambient", "Minimalist", "Experimental"];
    vibe = "Sophisticated, subtle, atmospheric";
  }

  return {
    genres: genres.slice(0, 4),
    vibe
  };
};

// Time of Day Associations
export const getColorTimeOfDay = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let timeOfDay = "";
  let lighting = "";
  let mood = "";

  if (l > 90) {
    // Very light
    timeOfDay = "High Noon";
    lighting = "Bright, direct sunlight, harsh shadows";
    mood = "Energetic, clear, exposed";
  } else if ((h >= 15 && h < 45) && l > 60 && s > 50) {
    // Orange/peach light
    timeOfDay = "Golden Hour (Sunrise)";
    lighting = "Warm, soft, directional light";
    mood = "Fresh, hopeful, new beginnings";
  } else if ((h >= 15 && h < 45) && l > 40 && l < 70 && s > 60) {
    // Orange/amber
    timeOfDay = "Golden Hour (Sunset)";
    lighting = "Warm, glowing, long shadows";
    mood = "Romantic, reflective, peaceful";
  } else if ((h >= 255 || h < 15) && l < 40 && s > 40) {
    // Red/purple dark
    timeOfDay = "Dusk";
    lighting = "Fading light, purple-pink sky";
    mood = "Transitional, mysterious, contemplative";
  } else if ((h >= 165 && h < 255) && l < 30) {
    // Dark blue
    timeOfDay = "Night / Midnight";
    lighting = "Moonlight, artificial lights, deep shadows";
    mood = "Quiet, intimate, mysterious";
  } else if ((h >= 165 && h < 255) && l > 60 && s < 40) {
    // Light blue
    timeOfDay = "Dawn / Blue Hour";
    lighting = "Soft, cool, diffused light";
    mood = "Calm, serene, awakening";
  } else if (l < 20) {
    // Very dark
    timeOfDay = "Late Night / Pre-Dawn";
    lighting = "Minimal light, deep darkness";
    mood = "Silent, isolated, introspective";
  } else if (l > 70 && s < 30) {
    // Light neutral
    timeOfDay = "Overcast Day";
    lighting = "Diffused, soft, even light";
    mood = "Calm, neutral, subdued";
  } else if (l > 50 && l < 75) {
    // Mid-tone
    timeOfDay = "Afternoon";
    lighting = "Steady, natural daylight";
    mood = "Active, productive, clear";
  } else {
    timeOfDay = "Morning";
    lighting = "Fresh, moderate light";
    mood = "Optimistic, energizing, clear";
  }

  return {
    timeOfDay,
    lighting,
    mood
  };
};

// Weather Associations
export const getColorWeatherAssociations = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let weather = [];
  let atmosphere = "";

  if (s < 15 && l > 80) {
    // Very light gray/white
    weather = ["Foggy", "Misty", "Overcast"];
    atmosphere = "Soft, diffused, mysterious";
  } else if (s < 20 && l > 50 && l < 80) {
    // Mid gray
    weather = ["Cloudy", "Overcast", "Drizzle"];
    atmosphere = "Subdued, calm, contemplative";
  } else if (s < 20 && l < 40) {
    // Dark gray
    weather = ["Stormy", "Heavy Rain", "Thunder"];
    atmosphere = "Dramatic, intense, powerful";
  } else if ((h >= 165 && h < 255) && s > 50 && l > 60) {
    // Bright blue
    weather = ["Clear Sky", "Sunny", "Perfect Day"];
    atmosphere = "Crisp, clear, uplifting";
  } else if ((h >= 165 && h < 255) && l < 40) {
    // Dark blue
    weather = ["Night", "Clear Night Sky", "Cold"];
    atmosphere = "Cool, serene, still";
  } else if (l > 90) {
    // Very bright
    weather = ["Bright Sunshine", "Heat Wave", "Glare"];
    atmosphere = "Intense, hot, exposed";
  } else if ((h >= 45 && h < 75) && s > 60) {
    // Yellow/gold
    weather = ["Golden Sunshine", "Warm Day", "Summer"];
    atmosphere = "Warm, cheerful, vibrant";
  } else if ((h >= 15 && h < 45) && s > 50) {
    // Orange
    weather = ["Sunset", "Warm Evening", "Dust Storm"];
    atmosphere = "Warm, glowing, transitional";
  } else if ((h >= 75 && h < 135) && s > 40) {
    // Green
    weather = ["Spring Rain", "Fresh After Rain", "Humid"];
    atmosphere = "Fresh, lush, growing";
  } else if ((h >= 345 || h < 15) && s > 50) {
    // Red
    weather = ["Heat", "Fire Weather", "Extreme Sunset"];
    atmosphere = "Hot, intense, dramatic";
  } else {
    weather = ["Mild", "Calm", "Pleasant"];
    atmosphere = "Comfortable, stable, moderate";
  }

  return {
    weather: weather.slice(0, 3),
    atmosphere
  };
};

// Emotional Arousal Level
export const getColorEmotionalArousal = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let arousalLevel = "";
  let energyRating = 0; // 1-10 scale
  let effect = "";
  let useCases = [];

  // Calculate arousal based on saturation and hue
  const warmHue = (h >= 0 && h <= 60) || (h >= 300 && h <= 360);
  const highSaturation = s > 70;
  const moderateLightness = l > 30 && l < 70;

  if (warmHue && highSaturation && moderateLightness) {
    // High arousal - warm saturated colors
    arousalLevel = "High Arousal / Energizing";
    energyRating = 8 + Math.floor(s / 20);
    effect = "Stimulating, exciting, increases heart rate and energy";
    useCases = ["Gyms", "Sports venues", "Call-to-action buttons", "Energy drinks"];
  } else if ((h >= 345 || h < 15) && s > 60) {
    // Red - highest arousal
    arousalLevel = "Very High Arousal / Stimulating";
    energyRating = 9;
    effect = "Maximum stimulation, urgency, excitement, can increase appetite";
    useCases = ["Emergency signals", "Sale signs", "Fast food", "Warning labels"];
  } else if ((h >= 165 && h < 255) && s > 40 && l < 50) {
    // Blue - calming
    arousalLevel = "Low Arousal / Calming";
    energyRating = 3;
    effect = "Reduces stress, lowers blood pressure, promotes calm focus";
    useCases = ["Bedrooms", "Hospitals", "Spas", "Meditation apps"];
  } else if ((h >= 75 && h < 135) && s > 40) {
    // Green - balanced
    arousalLevel = "Medium Arousal / Balanced";
    energyRating = 5;
    effect = "Restful yet refreshing, promotes balance and harmony";
    useCases = ["Offices", "Healthcare", "Eco-brands", "Wellness centers"];
  } else if (s < 20) {
    // Neutral - very calming
    arousalLevel = "Very Low Arousal / Neutral";
    energyRating = 2;
    effect = "Minimal emotional response, sophisticated, professional";
    useCases = ["Corporate offices", "Luxury brands", "Minimalist design"];
  } else if ((h >= 45 && h < 75) && s > 60) {
    // Yellow - moderately arousing
    arousalLevel = "Medium-High Arousal / Attention-Grabbing";
    energyRating = 7;
    effect = "Stimulates mental activity, grabs attention, optimistic";
    useCases = ["Caution signs", "Children's products", "Highlighting"];
  } else if ((h >= 255 && h < 285)) {
    // Purple - moderate
    arousalLevel = "Medium Arousal / Contemplative";
    energyRating = 4;
    effect = "Encourages creativity and introspection";
    useCases = ["Creative studios", "Luxury goods", "Spiritual spaces"];
  } else {
    arousalLevel = "Moderate Arousal / Neutral";
    energyRating = 5;
    effect = "Balanced emotional response";
    useCases = ["General use", "Versatile applications"];
  }

  return {
    arousalLevel,
    energyRating,
    effect,
    useCases: useCases.slice(0, 4)
  };
};

// Enhanced Accessibility Analysis (Beyond WCAG)
export const getColorAccessibilityEnhanced = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let colorBlindFriendly = "";
  let autismFriendly = "";
  let ageConsideration = "";
  let lowVisionSupport = "";

  // Color blind friendliness
  if (h >= 165 && h < 255) {
    // Blue - generally safe for most color blindness
    colorBlindFriendly = "Excellent - Blue is distinguishable in all types of color blindness";
  } else if ((h >= 45 && h < 75) || (h >= 345 || h < 15)) {
    // Yellow or Red
    colorBlindFriendly = "Good - Use with blue/yellow contrast, avoid red-green combinations";
  } else if (h >= 75 && h < 165) {
    // Green
    colorBlindFriendly = "Caution - Can be confused with red or brown in protanopia/deuteranopia";
  } else {
    colorBlindFriendly = "Fair - Consider adding patterns or icons for clarity";
  }

  // Autism-friendly colors (avoid overwhelming sensory input)
  if (s < 40 && l > 50 && l < 80) {
    autismFriendly = "Excellent - Muted, calming colors reduce sensory overload";
  } else if (s > 80 && l > 50 && l < 80) {
    autismFriendly = "Caution - Highly saturated colors may be overwhelming";
  } else if (l > 85) {
    autismFriendly = "Good - Very light colors are generally calming";
  } else {
    autismFriendly = "Fair - Use in moderation for neurodiverse audiences";
  }

  // Age-related vision considerations
  if (l > 70 && s > 50) {
    ageConsideration = "Good for older adults - High luminance and saturation aid visibility";
  } else if (l < 30 || s < 20) {
    ageConsideration = "Difficult for older adults - Consider higher contrast alternatives";
  } else if ((h >= 165 && h < 255) && l > 50) {
    ageConsideration = "Fair - Blue perception decreases with age, ensure good contrast";
  } else {
    ageConsideration = "Suitable for most age groups with proper contrast";
  }

  // Low vision support
  if (l > 80 || l < 20) {
    lowVisionSupport = "Excellent for contrast - High luminance contrast with opposite values";
  } else if (s > 60) {
    lowVisionSupport = "Good - High saturation aids visibility";
  } else {
    lowVisionSupport = "Moderate - Ensure high contrast with background colors";
  }

  return {
    colorBlindFriendly,
    autismFriendly,
    ageConsideration,
    lowVisionSupport
  };
};

// Religious Symbolism
export const getColorReligiousSymbolism = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let symbolism = {
    christianity: "",
    islam: "",
    hinduism: "",
    buddhism: "",
    judaism: ""
  };

  if (h >= 345 || h < 15) {
    // Red
    symbolism.christianity = "Blood of Christ, martyrdom, Pentecost, divine love";
    symbolism.islam = "Not traditionally significant";
    symbolism.hinduism = "Shakti (power), purity, sensuality";
    symbolism.buddhism = "Life force, compassion, sacred ceremonies";
    symbolism.judaism = "Sacrifice, sin, Passover";
  } else if (s < 15 && l > 85) {
    // White
    symbolism.christianity = "Purity, holiness, resurrection, glory";
    symbolism.islam = "Purity, peace";
    symbolism.hinduism = "Knowledge, purity, peace";
    symbolism.buddhism = "Purity, spiritual perfection";
    symbolism.judaism = "Purity, holiness, redemption";
  } else if (s < 15 && l < 20) {
    // Black
    symbolism.christianity = "Death, mourning, sin, penance";
    symbolism.islam = "Modesty, Ka'aba covering (kiswah)";
    symbolism.hinduism = "Time, Kali (goddess), destruction";
    symbolism.buddhism = "Darkness, ignorance";
    symbolism.judaism = "Mourning, humility";
  } else if (h >= 165 && h < 255) {
    // Blue
    symbolism.christianity = "Heaven, truth, the Virgin Mary";
    symbolism.islam = "Paradise, immortality, spirituality";
    symbolism.hinduism = "Krishna, Vishnu, infinity";
    symbolism.buddhism = "Wisdom, Medicine Buddha";
    symbolism.judaism = "Divinity, sky, commandments (tallit)";
  } else if (h >= 75 && h < 165) {
    // Green
    symbolism.christianity = "Growth, life, resurrection, hope";
    symbolism.islam = "Paradise, Prophet Muhammad, fertility";
    symbolism.hinduism = "Life, happiness, harmony";
    symbolism.buddhism = "Balance, harmony, Green Tara";
    symbolism.judaism = "Eternal life, hope";
  } else if (h >= 45 && h < 75) {
    // Yellow/Gold
    symbolism.christianity = "Divine glory, resurrection, sun";
    symbolism.islam = "Wisdom, prosperity";
    symbolism.hinduism = "Knowledge, learning, turmeric, sacred";
    symbolism.buddhism = "Rootlessness, monks' robes, enlightenment";
    symbolism.judaism = "Joy, Jerusalem";
  } else if (h >= 255 && h < 285) {
    // Purple
    symbolism.christianity = "Royalty, penance, Lent, Advent";
    symbolism.islam = "Not traditionally significant";
    symbolism.hinduism = "Third eye chakra, spirituality";
    symbolism.buddhism = "Mysticism, highest spiritual attainment";
    symbolism.judaism = "Royalty, redemption";
  } else if (h >= 15 && h < 45) {
    // Orange
    symbolism.christianity = "Endurance, strength";
    symbolism.islam = "Humility, sacrifice";
    symbolism.hinduism = "Fire, purity, saffron robes";
    symbolism.buddhism = "Monks' robes, illumination, transformation";
    symbolism.judaism = "Fire of faith";
  } else {
    // Pink/Magenta
    symbolism.christianity = "Joy, happiness";
    symbolism.islam = "Beauty";
    symbolism.hinduism = "Feminine divine energy";
    symbolism.buddhism = "Lotus, spiritual awakening";
    symbolism.judaism = "Love, affection";
  }

  return symbolism;
};

// Seasonal Lighting Conditions
export const getColorSeasonalLighting = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let springLight = "";
  let summerLight = "";
  let autumnLight = "";
  let winterLight = "";

  // How this color appears/shifts in different seasonal lighting

  if (l > 75) {
    // Light colors
    springLight = "Appears fresh and crisp in bright spring light";
    summerLight = "May appear washed out in intense summer sun";
    autumnLight = "Takes on warm undertones in golden autumn light";
    winterLight = "Appears cooler and brighter in winter's diffused light";
  } else if (l < 30) {
    // Dark colors
    springLight = "Appears deeper with strong directional spring light";
    summerLight = "Maintains depth, shows subtle color variation in strong light";
    autumnLight = "Richest appearance in warm, low-angle autumn sun";
    winterLight = "Appears even darker in short winter days, needs good lighting";
  } else if (h >= 15 && h < 75) {
    // Warm colors
    springLight = "Vibrant and energetic in spring's clear light";
    summerLight = "Most saturated and intense in summer sunlight";
    autumnLight = "Perfect harmony with autumn's golden light";
    winterLight = "Provides warmth contrast against cool winter light";
  } else if (h >= 165 && h < 255) {
    // Cool colors
    springLight = "Cool and refreshing, complements spring blooms";
    summerLight = "Provides cooling visual effect in hot summer";
    autumnLight = "Creates beautiful contrast with warm autumn tones";
    winterLight = "Most natural appearance in winter's blue-tinted light";
  } else {
    // Mid-tones
    springLight = "Balanced appearance in moderate spring light";
    summerLight = "Slightly more vibrant in strong summer daylight";
    autumnLight = "Subtle warm shift in autumn's golden hour";
    winterLight = "Slight cool shift in winter's indirect light";
  }

  return {
    springLight,
    summerLight,
    autumnLight,
    winterLight
  };
};

// Paint Finish Recommendations
export const getColorPaintFinish = (hex) => {
  const [h, s, l] = hexToHsl(hex);

  let recommendedFinish = "";
  let matteSuitability = "";
  let glossSuitability = "";
  let satinSuitability = "";
  let specialFinishes = [];

  if (l < 25) {
    // Dark colors
    recommendedFinish = "Matte or Eggshell";
    matteSuitability = "Excellent - Hides imperfections, sophisticated look";
    glossSuitability = "Dramatic - Shows every flaw but creates stunning depth";
    satinSuitability = "Good - Subtle sheen adds dimension";
    specialFinishes = ["Chalkboard paint", "Magnetic paint", "Metallic accents"];
  } else if (l > 75) {
    // Light colors
    recommendedFinish = "Satin or Semi-Gloss";
    matteSuitability = "Good - Clean, modern, but may show marks";
    glossSuitability = "Excellent for trim - Reflects light beautifully";
    satinSuitability = "Excellent - Easy to clean, soft sheen";
    specialFinishes = ["Pearl finish", "High-gloss lacquer", "Metallic pearl"];
  } else if (s > 70) {
    // Highly saturated
    recommendedFinish = "Satin or Gloss";
    matteSuitability = "Fair - Dulls the vibrant color";
    glossSuitability = "Excellent - Maximizes color intensity and depth";
    satinSuitability = "Good - Balances vibrancy with practicality";
    specialFinishes = ["High-gloss lacquer", "Automotive finish", "Metallic"];
  } else if (s < 30) {
    // Muted/neutral
    recommendedFinish = "Matte or Flat";
    matteSuitability = "Excellent - Enhances sophisticated, subtle quality";
    glossSuitability = "Not recommended - Unnecessary shine";
    satinSuitability = "Good for high-traffic areas";
    specialFinishes = ["Limewash", "Clay paint", "Venetian plaster"];
  } else {
    // Mid-range
    recommendedFinish = "Eggshell or Satin";
    matteSuitability = "Good - Versatile, hides flaws";
    glossSuitability = "Fair - Use for accents only";
    satinSuitability = "Excellent - Best of both worlds";
    specialFinishes = ["Eggshell", "Pearl", "Soft sheen"];
  }

  // Room-specific recommendations
  let roomRecommendations = [];
  if (l > 75) {
    roomRecommendations = ["Bathrooms: Semi-gloss (easy to clean)", "Kitchens: Satin (wipeable)", "Bedrooms: Matte/Eggshell"];
  } else if (l < 30) {
    roomRecommendations = ["Accent walls: Matte", "Dining rooms: Eggshell", "Home theaters: Matte"];
  } else {
    roomRecommendations = ["Living rooms: Eggshell/Satin", "Hallways: Satin", "Any room: Versatile"];
  }

  return {
    recommendedFinish,
    matteSuitability,
    glossSuitability,
    satinSuitability,
    specialFinishes: specialFinishes.slice(0, 3),
    roomRecommendations: roomRecommendations.slice(0, 3)
  };
};